import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User, Login, MetadataResponse, SuccessResponse, ChangePassword } from '../models/users.model';
import { Observable } from 'rxjs';
import * as constants from '../../assets/config/constants.json';
import { RolePermissions, UpdateRolePermissions } from '../models/permissions.model';


@Injectable({
  providedIn: 'root'
})
export class HttpServiceService {
  baseUrl! : string;
  logoUrl? : string = '';
  headerLogoUrl? : string = '';
  constructor(private http: HttpClient) { }

  setBaseUrl(url : string): void {
    this.baseUrl = url;
  }

  setLogoUrl(url : string) :void{
    this.logoUrl = url;
  }

  getLogoUrl(){
    return this.logoUrl;
  }

  setHeaderLogoUrl(url : string) :void{
    this.headerLogoUrl = url;
  }

  getHeaderLogoUrl(){
    return this.headerLogoUrl;
  }

  getMetadata(){
    return this.http.get<MetadataResponse>(this.baseUrl + constants.metadata);
  }

  loginUser(user : Login) : Observable<any>{
    return this.http.post(this.baseUrl+constants.login, user );
  }

  getAllUsers(page: number, limit : number, query : string = '') : Observable<any>{
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if(query) defaultQuery = defaultQuery + query;
    return this.http.get(this.baseUrl + constants.allUsers + defaultQuery);
  }

  addUser(user : User): Observable<any>{
    return this.http.post(this.baseUrl + constants.allUsers, user);
  }

  editUser(user : any, userId : string): Observable<any>{
    return this.http.put(this.baseUrl + constants.allUsers+"/"+userId, user);
  }

  getUser(userId: string) : Observable<any>{
    return this.http.get<SuccessResponse>(this.baseUrl + constants.allUsers+"/"+userId);
  }

  deleteUser(userId : string): Observable<any>{
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.allUsers+"/"+userId);
  }

  getPermissions() : Observable<any> {
    return this.http.get<RolePermissions>(this.baseUrl+constants.getPermissions );
  }

  getRolePermissions(roleIdentifier : string) : Observable<any> {
    return this.http.get<RolePermissions>(this.baseUrl + constants.rolePermissions+"/"+ roleIdentifier);
  }

  updateRolePermissions(rolePermissions : UpdateRolePermissions) : Observable<any>  {
    return this.http.put(this.baseUrl + constants.rolePermissions, rolePermissions);
  }

  vendorOwnerRegistration(data : any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.register, data);
  }

  forgotPassword(data : { email : string }){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.forgotPassword, data);
  }

  resetPassword(data : any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.resetPassword, data);
  }

  getLocations(type : string ,data : string = ''){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.locations + "?type="+ type + data);
  }

  userLogout(){
    return this.http.post(this.baseUrl + constants.logout, '');
  }

  verifyEmail(data : any){
    return this.http.post(this.baseUrl + constants.verifyEmail, data);
  }

  changePassword(data : ChangePassword){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.changePassword, data);
  }

  getAllProperties(page: number, limit : number, query : any = ''){
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if(query) defaultQuery = defaultQuery + query;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + defaultQuery);
  }

  addProperty(data : any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.properties, data);
  }

  getPropertyCities(){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + "/cities");
  }

   getPropertyStates(){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + "/states");
  }

  getPropertyDetailsById(uid : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + `/${uid}`);
  }

  updateProperty(uid: string, property : any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.properties + `/${uid}`, property);
  }

  getBillCategories(billType : number){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.billCategories + `/${billType}`);
  }

  getAdminBillCategories(billType?: number){
    const query = billType ? `?bill_type=${billType}` : '';
    return this.http.get<SuccessResponse>(this.baseUrl + constants.adminBillCategories + query);
  }

  createAdminBillCategory(data: any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.adminBillCategories, data);
  }

  updateAdminBillCategory(id: number, data: any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.adminBillCategories + `/${id}`, data);
  }

  deleteAdminBillCategory(id: number){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.adminBillCategories + `/${id}`);
  }

  getAdminItems(search?: string) {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.http.get<SuccessResponse>(this.baseUrl + constants.adminItems + query);
  }

  createAdminItem(data: any) {
    return this.http.post<SuccessResponse>(this.baseUrl + constants.adminItems, data);
  }

  updateAdminItem(id: number, data: any) {
    return this.http.put<SuccessResponse>(this.baseUrl + constants.adminItems + `/${id}`, data);
  }

  deleteAdminItem(id: number) {
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.adminItems + `/${id}`);
  }

  addBill(data : any, propertyId : string){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/bills`, data);
  }

  getAllBills(propertyId : string, page: number, limit : number, query : any = ''){
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if(query) defaultQuery = defaultQuery + query;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/bills` + defaultQuery);
  }

  getBillDetailsById(propertyId : string, billId : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/bills/${billId}`);
  }

  editBill(data : any, propertyId : string, billId : string){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/bills/${billId}`, data);
  }

  deleteBillImage(propertyId : string, billId : string, uid : string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/bills/${billId}/uploads/${uid}`);
  }

  generateInvoice(billId : string, propertyId : string){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/bills/${billId}/generate-invoice`, '');
  }

  regenerateInvoice(billId : string, propertyId : string){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/bills/${billId}/regenerate-pdf`, '');
  }

  deleteBill(billId : string, propertyId : string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/bills/${billId}`);
  }

  viewInvoice(billId : string, propertyId : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/bills/${billId}/view-invoice`);
  }

  getBillImages(url : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.billImages + `?key=${url}`);
  }

  addDocument(data : any, propertyId : string){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/documents`, data);
  }

  getAllPropertyDocuments(propertyId : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/documents`);
  }

  deletePropertyDocument(uid : string, propertyId : string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/documents/${uid}`);
  }

  getPropertyBuyers(propertyId : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/buyer-details`);
  }

  addPropertyBuyer(data : any, propertyId : string){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/buyer-details`, data);
  }

  updatePropertyBuyer(data : any, propertyId : string, uid : string){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/buyer-details/${uid}`, data);
  }

  deletePropertyBuyer(uid : string, propertyId : string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/buyer-details/${uid}`);
  }

  addLease(data : any, propertyId : string){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/leases`, data);
  }

  getAllLeases( propertyId : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/leases`);
  }

  getLeaseDetailsById(uid : string, propertyId : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/leases/${uid}`);
  }

  editLease(data : any, propertyId : string, uid : string){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/leases/${uid}`, data);
  }

  renewLease(uid : string, propertyId : string){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/leases/${uid}/renew`, '');
  }

  archiveLease(uid : string, propertyId : string){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/leases/${uid}/archive`, '');
  }

  uploadPropertyImages(data : any, propertyId : string){
     return this.http.post<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/images`, data);
  }

  deletePropertyImage(uid : string, propertyId : string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/images/${uid}`);
  }

  getAllPropertyImages(propertyId : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/images`);
  }

  listingImageStatus(data : any, propertyId : string){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/images/listing-status`, data);
  }

  getAllOwners(page: number, limit : number, query : any = ''){
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if(query) defaultQuery = defaultQuery + query;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.allUsers + `/owners` + defaultQuery);
  }

  getAllTenants(page: number, limit : number, query : any = ''){
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if(query) defaultQuery = defaultQuery + query;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.allUsers + `/tenants` + defaultQuery);
  }

  getUserPermissions(){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.allUsers + "/permissions");
  }

  getAllPropertyItems(propertyId : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/rooms/items`);
  }

  savePropertyRooms(propertyId : string, data : any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/rooms`, data);
  }

  getPropertyRoomItems(propertyId : string, roomId : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/rooms/${roomId}/items`);
  }

  getAllPropertyRooms(propertyId : string){
      return this.http.get<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/rooms`);
  }

  updatePropertyRoomItems(propertyId : string,roomId : string, itemId : string, data : any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/rooms/${roomId}/items/${itemId}`, data);
  }

  updatePropertyRoom(propertyId : string, roomId : string, data : any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/rooms/${roomId}`, data);
  }

  deletePropertyRoom(propertyId : string, roomId : string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/rooms/${roomId}`);
  }

  createInspection(data : any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.inspections, data);
  }

  getAllInspections(page: number, limit : number, query : any = ''){
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if(query) defaultQuery = defaultQuery + query;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.inspections + defaultQuery);
  }

  getInspectionDetailsById(id : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.inspections + `/${id}`);
  }

  updateInspection(id:string, data : any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.inspections + `/${id}`, data);
  }

  updateInspectionStatus(id:string, data : any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.inspections + `/${id}/status`, data);
  }

  deleteInspectionUploads(id:string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.inspections + `/uploads/${id}`);
  }

  deleteInspection(id : string){
     return this.http.delete<SuccessResponse>(this.baseUrl + constants.inspections + `/${id}`);
  }

  downloadInspection(id : string){
     return this.http.get(this.baseUrl + constants.inspections + `/${id}/download`, { responseType: 'blob' });
  }

  getRoomsByInspectionId(id : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.inspections + `/${id}/rooms`);
  }

  saveInspectionsRoom(id : string, data : any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.inspections + `/${id}/rooms`, data);
  }

  updateInspectionsRoom(id : string, roomId : string, data : any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.inspections + `/${id}/rooms/${roomId}`, data);
  }

  getInspectionRoomItems(inspectionId : string, roomId : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.inspections + `/${inspectionId}/rooms/${roomId}/items`);
  }

  deleteInspectionImage(imageId : string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.inspections + `/item/uploads/${imageId}`);
  }

  updateInspectionRoomItems(inspectionId : string, roomId : string, itemId : string, data : any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.inspections + `/${inspectionId}/rooms/${roomId}/items/${itemId}`, data);
  }

  deleteInspectionRoom(inspectionId : string, roomId : string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.inspections + `/${inspectionId}/rooms/${roomId}`);
  }

  shareInspectionPdf(inspectionId: string, shareData: { share_type: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    return this.http.post<SuccessResponse>(this.baseUrl + constants.inspections + `/${inspectionId}/share`, shareData);
  }

  saveServiceRequest(data : any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants['service-requests'] , data);
  }

  getAllServiceRequests(page: number, limit : number, query : any = ''){
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if(query) defaultQuery = defaultQuery + query;
    return this.http.get<SuccessResponse>(this.baseUrl + constants['service-requests'] + defaultQuery);
  }

  getTicketDetailsById(id : string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants['service-requests']+`/${id}`);
  }

  updateServiceRequest(id : string , data : any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants['service-requests']+`/${id}` , data);
  }

  deleteTicketsUploads(id : string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants['service-requests'] + `/uploads/${id}`);
  }

  deleteServiceTicket(id : string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants['service-requests'] + `/${id}`);
  }

  updatePropertyPlatforms(propertyId : string, data : any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/platforms`, data);
  }

  getAllNotifications() {
    return this.http.get<SuccessResponse>(this.baseUrl + constants.notifications);
  }

  updateNotification(data: any) {
    return this.http.put<SuccessResponse>(this.baseUrl + constants.notifications, data);
  }

  verifyRazorpaySignature(propertyId : string, billId : string,data: any) {
    return this.http.post<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/bills/${billId}/verify-payment`, data);
  }

  initiatePayment(propertyId : string, billId : string, data :any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.properties + `/${propertyId}/bills/${billId}/initiate-payment`, data);
  }

  getAllAmenities(){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.amenities);
  }

  getAllEnquiries(
    page: number,
    limit: number,
    sortBy: string = 'created_at',
    orderBy: string = 'DESC',
    queryParams: string = ''
  ) {
    let defaultQuery = `?page=${page}&limit=${limit}&sortBy=${sortBy}&orderBy=${orderBy}`;
    if (queryParams) {
      defaultQuery += queryParams;
    }
    return this.http.get<SuccessResponse>(
      this.baseUrl + constants.enquiries + defaultQuery
    );
  }

  getEnquiryDetailsById(id: string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.enquiries + `/${id}`);
  }

  getEnquiryComments(enquiryId: string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.enquiries + `/${enquiryId}/comments`);
  }

  saveEnquiryComment(enquiryId: string, data: any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.enquiries + `/${enquiryId}/comments`, data);
  }

  updateEnquiry(id: string, data: any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.enquiries + `/${id}`, data);
  }

  createEnquiry(data: any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.enquiries, data);
  }

  getAllLeads(
    page: number,
    limit: number,
    sortBy: string = 'created_at',
    orderBy: string = 'DESC',
    queryParams: string = ''
  ) {
    let defaultQuery = `?page=${page}&limit=${limit}&sortBy=${sortBy}&orderBy=${orderBy}`;
    if (queryParams) {
      defaultQuery += queryParams;
    }
    return this.http.get<SuccessResponse>(
      this.baseUrl + constants.leads + defaultQuery
    );
  }

  getLeadDetailsById(id: string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.leads + `/${id}`);
  }

  deleteLeadUpload(uid: string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.leads + `/uploads/${uid}`);
  }

  createLead(data: any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.leads, data);
  }

  updateLead(id: string, data: any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.leads + `/${id}`, data);
  }

  deleteLead(id: string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.leads + `/${id}`);
  }

  getLeadComments(leadId: string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.leads + `/${leadId}/comments`);
  }

  saveLeadComment(leadId: string, data: any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.leads + `/${leadId}/comments`, data);
  }

  getLeadHistory(leadId: string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.leads + `/${leadId}/history`);
  }

  // Tasks CRUD
  getAllTasks(
    page: number,
    limit: number,
    sortBy: string = 'created_at',
    orderBy: string = 'DESC',
    queryParams: string = ''
  ) {
    let defaultQuery = `?page=${page}&limit=${limit}&sortBy=${sortBy}&orderBy=${orderBy}`;
    if (queryParams) {
      defaultQuery += queryParams;
    }
    return this.http.get<SuccessResponse>(
      this.baseUrl + constants.tasks + defaultQuery
    );
  }

  getTaskDetailsById(id: string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.tasks + `/${id}`);
  }

  deleteTaskUpload(uid: string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.tasks + `/uploads/${uid}`);
  }

  createTask(data: any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.tasks, data);
  }

  updateTask(id: string, data: any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.tasks + `/${id}`, data);
  }

  deleteTask(id: string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.tasks + `/${id}`);
  }

  getTaskComments(taskId: string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.tasks + `/${taskId}/comments`);
  }

  saveTaskComment(taskId: string, data: any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.tasks + `/${taskId}/comments`, data);
  }

  getTaskHistory(taskId: string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.tasks + `/${taskId}/history`);
  }

  // Tasks — team task management (TASKS_CONTRACT §4)
  getTasksSummary(query: string = ''){
    const q = query ? `?${query.replace(/^&/, '')}` : '';
    return this.http.get<SuccessResponse>(this.baseUrl + constants.tasksSummary + q);
  }

  getMyDay(date: string = ''){
    const q = date ? `?date=${date}` : '';
    return this.http.get<SuccessResponse>(this.baseUrl + constants.tasksMyDay + q);
  }

  updateTaskStatus(taskId: string, data: { status: string; hold_reason?: string }){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.tasks + `/${taskId}/status`, data);
  }

  assignTask(taskId: string, data: { assignee_id: string }){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.tasks + `/${taskId}/assign`, data);
  }

  verifyTask(taskId: string){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.tasks + `/${taskId}/verify`, {});
  }

  taskCheckIn(taskId: string, data: any = {}){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.tasks + `/${taskId}/check-in`, data);
  }

  taskCheckOut(taskId: string, data: any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.tasks + `/${taskId}/check-out`, data);
  }

  getTaskChecklist(taskId: string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.tasks + `/${taskId}/checklist`);
  }

  updateTaskChecklist(taskId: string, data: { items: { id?: string; label: string }[] }){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.tasks + `/${taskId}/checklist`, data);
  }

  toggleTaskChecklistItem(taskId: string, itemId: string, is_done: boolean){
    return this.http.patch<SuccessResponse>(
      this.baseUrl + constants.tasks + `/${taskId}/checklist/${itemId}`, { is_done }
    );
  }

  getTaskActivity(taskId: string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.tasks + `/${taskId}/activity`);
  }

  // Activity logs (daily activity report)
  createActivityLog(data: any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.tasksActivityLogs, data);
  }

  getActivityLogs(page: number, limit: number, query: string = ''){
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (query) defaultQuery += query;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.tasksActivityLogs + defaultQuery);
  }

  exportActivityLogs(query: string = ''){
    const q = query ? `?${query.replace(/^&/, '')}` : '';
    return this.http.get(this.baseUrl + constants.tasksActivityLogs + '/export' + q, { responseType: 'blob' });
  }

  // Recurring task templates
  getAllTaskRecurrences(page: number, limit: number, query: string = ''){
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (query) defaultQuery += query;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.tasksRecurrences + defaultQuery);
  }

  getTaskRecurrenceById(uid: string){
    return this.http.get<SuccessResponse>(this.baseUrl + constants.tasksRecurrences + `/${uid}`);
  }

  createTaskRecurrence(data: any){
    return this.http.post<SuccessResponse>(this.baseUrl + constants.tasksRecurrences, data);
  }

  updateTaskRecurrence(uid: string, data: any){
    return this.http.put<SuccessResponse>(this.baseUrl + constants.tasksRecurrences + `/${uid}`, data);
  }

  deleteTaskRecurrence(uid: string){
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.tasksRecurrences + `/${uid}`);
  }

  // Partner admin users (owner dropdown data source for Tasks)
  getPartnerAdmins(page: number = 1, limit: number = 100, search: string = ''){
    let query = `&role_identifier=partner_admin`;
    if (search) {
      query += `&search=${encodeURIComponent(search)}`;
    }
    return this.getAllUsers(page, limit, query);
  }

  getUsersByPropertyRole(page: number = 1, limit: number = 50, search: string = ''){
    let query = `?page=${page}&limit=${limit}`;
    if (search) {
      query += `&search=${encodeURIComponent(search)}`;
    }
    return this.http.get<SuccessResponse>(this.baseUrl + constants.usersPropertyRoles + query);
  }

  getPropertyManagers(page: number = 1, limit: number = 50, search: string = ''){
    let query = `&role_identifier=property_manager`;
    if (search) {
      query += `&search=${encodeURIComponent(search)}`;
    }
    return this.getAllUsers(page, limit, query);
  }

  // Bank Accounts CRUD
  getBankAccounts(page: number = 1, limit: number = 50, search: string = '') {
    let query = `?page=${page}&limit=${limit}`;
    if (search) query += `&search=${encodeURIComponent(search)}`;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.bankAccounts + query);
  }

  getBankAccount(uid: string) {
    return this.http.get<SuccessResponse>(this.baseUrl + constants.bankAccounts + `/${uid}`);
  }

  createBankAccount(payload: any) {
    return this.http.post<SuccessResponse>(this.baseUrl + constants.bankAccounts, payload);
  }

  updateBankAccount(uid: string, payload: any) {
    return this.http.put<SuccessResponse>(this.baseUrl + constants.bankAccounts + `/${uid}`, payload);
  }

  deleteBankAccount(uid: string) {
    return this.http.delete<SuccessResponse>(this.baseUrl + constants.bankAccounts + `/${uid}`);
  }

  getBankAccountSequences(uid: string) {
    return this.http.get<SuccessResponse>(this.baseUrl + constants.bankAccounts + `/${uid}/sequences`);
  }

  resyncBankAccountSequences(uid: string) {
    return this.http.post<SuccessResponse>(this.baseUrl + constants.bankAccounts + `/${uid}/resync-sequences`, {});
  }

  // Reports APIs
  getInspectionReportAll(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/inspections/all' + defaultQuery);
  }

  getInspectionReportByManager(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/inspections/by-manager' + defaultQuery);
  }

  downloadInspectionReportAll(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/inspections/all/download' + query, payload);
  }

  downloadInspectionReportByManager(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/inspections/by-manager/download' + query, payload);
  }

  getReportJobStatus(jobId: string) {
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reportJobs + '/' + jobId);
  }

  getAccountsReportAll(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/accounts/all' + defaultQuery);
  }

  getAccountsReportByManager(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/accounts/by-manager' + defaultQuery);
  }

  getAccountsRentBilling(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/accounts/rent-billing' + defaultQuery);
  }

  downloadAccountsRentBilling(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/accounts/rent-billing/download' + query, payload);
  }

  getAccountsReportByProperty(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/accounts/by-property' + defaultQuery);
  }

  downloadAccountsReportAll(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/accounts/all/download' + query, payload);
  }

  downloadAccountsReportByManager(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/accounts/by-manager/download' + query, payload);
  }

  downloadAccountsReportByProperty(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/accounts/by-property/download' + query, payload);
  }

  getLeadsReportAll(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/leads/all' + defaultQuery);
  }

  getLeadsReportByStatus(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/leads/by-status' + defaultQuery);
  }

  getLeadsReportBySource(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/leads/by-source' + defaultQuery);
  }

  downloadLeadsReportAll(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/leads/all/download' + query, payload);
  }

  downloadLeadsReportByStatus(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/leads/by-status/download' + query, payload);
  }

  downloadLeadsReportBySource(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/leads/by-source/download' + query, payload);
  }

  getEnquiriesReportAll(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/enquiries/all' + defaultQuery);
  }

  getEnquiriesReportByStatus(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/enquiries/by-status' + defaultQuery);
  }

  getEnquiriesReportByPlatform(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/enquiries/by-platform' + defaultQuery);
  }

  downloadEnquiriesReportAll(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/enquiries/all/download' + query, payload);
  }

  downloadEnquiriesReportByStatus(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/enquiries/by-status/download' + query, payload);
  }

  downloadEnquiriesReportByPlatform(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/enquiries/by-platform/download' + query, payload);
  }

  // Properties Reports APIs
  getPropertiesReportAll(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/properties/all' + defaultQuery);
  }

  downloadPropertiesReportAll(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/properties/all/download' + query, payload);
  }

  // Lease Reports APIs
  getLeaseReportAll(page: number, limit: number, queryParams: string = '') {
    let defaultQuery = `?page=${page}&limit=${limit}`;
    if (queryParams) defaultQuery += queryParams;
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/leases/all' + defaultQuery);
  }

  getLeaseReportByManager(managedBy: string = '') {
    const query = managedBy ? `?managed_by=${managedBy}` : '';
    return this.http.get<SuccessResponse>(this.baseUrl + constants.reports + '/leases/by-manager' + query);
  }

  downloadLeaseReportAll(fileType: string, queryParams: string = '', shareData?: { share_type?: string; email?: string; whatsapp?: string; first_name?: string; last_name?: string; country_code?: string }) {
    let query = queryParams ? `?${queryParams.substring(1)}` : '';
    const payload: any = { file_type: fileType };
    if (shareData?.share_type) payload.share_type = shareData.share_type;
    if (shareData?.email) payload.email = shareData.email;
    if (shareData?.whatsapp) payload.whatsapp = shareData.whatsapp;
    if (shareData?.first_name) payload.first_name = shareData.first_name;
    if (shareData?.last_name) payload.last_name = shareData.last_name;
    if (shareData?.country_code) payload.country_code = shareData.country_code;
    return this.http.post<SuccessResponse>(this.baseUrl + constants.reports + '/leases/all/download' + query, payload);
  }

  // Templates
  getDocumentTemplates() {
    return this.http.get<SuccessResponse>(this.baseUrl + constants.documentTemplates);
  }

  updateDocumentTemplate(id: string, data: { html: string }) {
    return this.http.put<SuccessResponse>(this.baseUrl + constants.documentTemplates + `/${id}`, data);
  }

  getNotificationTemplates() {
    return this.http.get<SuccessResponse>(this.baseUrl + constants.notificationTemplates);
  }

  updateNotificationTemplate(id: number, data: any) {
    return this.http.put<SuccessResponse>(this.baseUrl + constants.notificationTemplates + `/${id}`, data);
  }

  // ===== Agent =====
  agentQuery(payload: { question: string; conversationId?: string }) {
    return this.http.post<SuccessResponse>(this.baseUrl + constants.agent + '/query', payload);
  }

  agentListConversations() {
    return this.http.get<SuccessResponse>(this.baseUrl + constants.agent + '/conversations');
  }

  agentGetConversation(uid: string) {
    return this.http.get<SuccessResponse>(this.baseUrl + constants.agent + `/conversations/${uid}`);
  }

}
